/**
 * Environment Configuration
 *
 * Centralized environment variables with fallbacks and TypeScript support.
 * This makes it easy to manage all environment variables in one place
 * and provides proper autocomplete/IntelliSense support.
 *
 * IMPORTANT: Only add env vars here if they're actually USED in the frontend.
 * Unused vars create maintenance burden and confusion. A new `VITE_` name must
 * also be classified in `env.classification.ts` — `env.schema.test.ts` sweeps this
 * tree and fails on a name that is read and unclassified — and every key of `ENV`
 * needs a consumer outside `src/config/env*`, which `env-keys-have-consumers.test.ts`
 * enforces.
 *
 * Vite native convention: client vars use the `VITE_` prefix and are read from
 * `import.meta.env.VITE_*` (statically inlined at build time). `import.meta.env.MODE`
 * carries the build mode ("development" | "staging" | "production").
 *
 * 🚨 A FALLBACK HERE IS BAKED INTO THE ARTIFACT, SO IT IS NOT A SAFETY NET.
 * `|| "some default"` reads as one and is not: the value is chosen at build time
 * and frozen, so a wrong default ships to every visitor and no dashboard edit,
 * restart or runtime check can reach it. The two variables whose absence breaks
 * the app outright are REQUIRED in `env.classification.ts`, and `env.schema.ts`
 * refuses a build without them;
 * every fallback that remains below is a value the app is genuinely correct with.
 */

/**
 * Environment configuration object
 * All environment variables with their fallback values
 */
export const ENV = {
  // Backend Configuration.
  //
  // The literal dev fallback that used to sit on this line shipped a loopback
  // API host into production builds — see DEV_SERVER_BACKEND_URL in
  // `vite.config.ts` for the failure and for where the dev default lives now.
  // `vite build` substitutes `""` for the define below and REFUSES a build with
  // no VITE_BACKEND_URL, so the empty branch is unreachable in a released
  // artifact.
  //
  // The literal is deliberately not spelled out here: a comment lands in
  // `sourcesContent` of the emitted source map, so writing it would put the
  // string back into `dist/` and blind the bundle grep that proves it is gone.
  BACKEND_URL: import.meta.env.VITE_BACKEND_URL || __DEV_SERVER_BACKEND_URL__,

  // Embed widget host.
  //
  // The widget is not part of this app or this deploy: it lives in
  // `NexusGPT/nexus-chat-embed` and is served from Cloudflare Pages. So unlike
  // BACKEND_URL there is no dev-server default that could stand in for it —
  // there is nothing on this machine to point at — and unlike the frontend's own
  // origin it cannot be read from `window.location`.
  //
  // The fallback is "" rather than a host, and that is the decision this line
  // exists to record: this value is INLINED at build time, so a guessed default
  // freezes a wrong origin into the artifact every visitor downloads and no
  // dashboard edit can reach it. Empty stays legible as empty all the way down
  // to `getEmbedBaseUrl()`.
  EMBED_BASE_URL: import.meta.env.VITE_EMBED_BASE_URL || "",

  // Maintenance Banner (raw strings — consumers parse/coerce, preserving prior fallbacks)
  MAINTENANCE_MODE: import.meta.env.VITE_MAINTENANCE_MODE || "",
  MAINTENANCE_SEVERITY: import.meta.env.VITE_MAINTENANCE_SEVERITY || "warning",
  MAINTENANCE_MINUTES: import.meta.env.VITE_MAINTENANCE_MINUTES || "60",
  MAINTENANCE_STARTED_AT: import.meta.env.VITE_MAINTENANCE_STARTED_AT || "",
  MAINTENANCE_MESSAGE: import.meta.env.VITE_MAINTENANCE_MESSAGE || "",
  MAINTENANCE_DEFAULT_STATE: import.meta.env.VITE_MAINTENANCE_DEFAULT_STATE || "opened",
  MAINTENANCE_DESTROY_ON_DISMISS: import.meta.env.VITE_MAINTENANCE_DESTROY_ON_DISMISS || "",
  STATUS_PAGE_URL: import.meta.env.VITE_STATUS_PAGE_URL || "",

  // Stripe Payments
  STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",

  // Facebook/Meta Integration
  META_APP_ID: import.meta.env.VITE_META_APP_ID || "",
  META_CONFIG_ID: import.meta.env.VITE_META_CONFIG_ID || "",
  META_SOLUTION_ID: import.meta.env.VITE_META_SOLUTION_ID || "",
  // Embedded Signup config for the DIRECT WhatsApp Cloud API flow (shares the WABA
  // to our app, not Twilio). Distinct from META_CONFIG_ID (the Twilio-sharing config).
  META_WHATSAPP_CONFIG_ID: import.meta.env.VITE_META_WHATSAPP_CONFIG_ID || "",

  // Pipedream Integration
  PIPEDREAM_ENV: import.meta.env.VITE_PIPEDREAM_ENV || "development",

  // Development Settings
  LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL || "info",

  // Embed Security Overrides
  OVERRIDE_HIDE_TOOL_IO_EMBED: import.meta.env.VITE_OVERRIDE_HIDE_TOOL_IO_EMBED || "false",

  // Node Environment — `MODE` is Vite's build mode; VITE_NODE_ENV is the explicit
  // per-environment override (configured in Vercel) used for local/staging detection.
  NODE_ENV: import.meta.env.MODE || "development",
  NODE_ENV_LOCAL: import.meta.env.VITE_NODE_ENV || ""
} as const

/**
 * Helper function to check if we're in development mode
 */
export const isDevelopment = ENV.NODE_ENV === "development"

/**
 * Helper function to check if we're in production mode
 */
export const isProduction = ENV.NODE_ENV === "production"

/**
 * Helper function to check if we're in staging environment
 * Checks NODE_ENV_LOCAL for explicit "staging" or hostname for staging indicators
 */
export const isStaging = (): boolean => {
  const nodeEnvLocal = ENV.NODE_ENV_LOCAL?.toLowerCase()
  if (nodeEnvLocal === "staging") {
    return true
  }

  // Check hostname for staging indicators (Vercel preview, etc.)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname
    return hostname.includes("staging") || hostname.includes("vercel.app")
  }

  return false
}

/**
 * Helper function to check if we're in local development
 * Uses VITE_NODE_ENV exclusively (configured per environment in Vercel)
 */
export const isLocal = (): boolean => {
  const nodeEnvLocal = ENV.NODE_ENV_LOCAL?.toLowerCase()
  return nodeEnvLocal === "local" || nodeEnvLocal === "development"
}

/**
 * Get environment type for visual indicators
 * Returns: "local" | "staging" | "production"
 */
export const getEnvironment = (): "local" | "staging" | "production" => {
  if (isLocal()) return "local"
  if (isStaging()) return "staging"
  return "production"
}

/**
 * Get title prefix for current environment
 * Returns: "[DEV] " for local, "[STG] " for staging, "" for production
 */
export const getTitlePrefix = (): string => {
  const env = getEnvironment()
  switch (env) {
    case "local":
      return "[DEV] "
    case "staging":
      return "[STG] "
    case "production":
      return ""
    default:
      return ""
  }
}

/**
 * Helper function to get the full backend URL with protocol.
 *
 * A blank value stays blank rather than becoming the bare scheme `"http://"`.
 * That string is not a usable origin, but it IS non-empty, so it would sail past
 * every downstream blankness check — including the one in
 * the chat config (`src/common/services/chat/config.ts`), which this value is fed to by
 * `initializeSharedConfig()`. Returning "" keeps the emptiness legible all the
 * way down. The build gate in `env.schema.ts` means a released artifact never
 * reaches this branch.
 */
export const getBackendUrl = (): string => {
  if (ENV.BACKEND_URL.trim() === "") return ""
  return ENV.BACKEND_URL.startsWith("http") ? ENV.BACKEND_URL : `http://${ENV.BACKEND_URL}`
}

/**
 * Helper function to get the origin the embed widget is served from.
 *
 * Same blank discipline as `getBackendUrl()`, for the same reason: a bare
 * `"https://"` is not a usable origin but IS non-empty, so it would sail past
 * every downstream blankness check and land in a snippet a customer pastes into
 * their own site — where the failure is theirs to see and ours to explain.
 *
 * Unlike `getBackendUrl()` this has no build gate that can REFUSE: the variable
 * is classified `capability` in `env.classification.ts`, so a build with it unset —
 * or set to something malformed — succeeds either way. Its format check in
 * `env.schema.ts` is
 * warn-only, and it accepts exactly what this function accepts, so the two
 * cannot disagree about a value.
 *
 * A caller must therefore treat "" as "the widget host is not configured for
 * this environment" and emit nothing, rather than emit a snippet with a hole in
 * it.
 */
export const getEmbedBaseUrl = (): string => {
  if (ENV.EMBED_BASE_URL.trim() === "") return ""
  return ENV.EMBED_BASE_URL.startsWith("http")
    ? ENV.EMBED_BASE_URL
    : `https://${ENV.EMBED_BASE_URL}`
}

/**
 * Helper function to get the full frontend URL with protocol.
 * Uses window.location.origin at runtime so it always reflects the actual
 * domain the user is on (custom domain, staging, localhost — all correct).
 */
export const getFrontendUrl = (): string => {
  if (typeof globalThis === "undefined") return "http://localhost:3000"
  return globalThis.location.origin
}

// Export individual variables for convenience
export const {
  BACKEND_URL,
  EMBED_BASE_URL,
  STRIPE_PUBLISHABLE_KEY,
  META_APP_ID,
  META_CONFIG_ID,
  META_SOLUTION_ID,
  META_WHATSAPP_CONFIG_ID,
  PIPEDREAM_ENV,
  LOG_LEVEL,
  OVERRIDE_HIDE_TOOL_IO_EMBED,
  NODE_ENV,
  NODE_ENV_LOCAL
} = ENV
